import { MailAddressModel } from 'api/swagger/ApiClient';
import { autoinject } from 'aurelia-framework';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { downloadFile } from 'framework/script_utilities/file-utils';
import { formatCsv, formatEmpty } from 'framework/script_utilities/text-utils';
import { UserLocation, UsersEmail } from 'model/users-locations';
import moment from 'moment';
import { UsersLocationsController } from 'screens/administration/locations/users-locations-controller';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { UsersEmailDialog } from './users-email-dialog';

@autoinject
export class UsersLocationsList {
  private usersLocationsTable: HTMLElement;

  constructor(private controller: UsersLocationsController, private toastHelper: ToastHelper, private dialogHelper: DialogHelper) {
  }

  attached() {
    this.controller.showUserLocationDetailInList = (userLocation: UserLocation) => this.showDetailInList(userLocation);
    const detailedUserLocation = this.controller.usersLocationsList.find(userLocation => userLocation.isDetail);
    if (detailedUserLocation)
      this.showDetailInList(detailedUserLocation);
    this.controller.setSplitter();
  }

  detached() {
    this.controller.showUserLocationDetailInList = null;
    this.controller.storeSplitter();
  }

  export() {
    const fileMimeType = "data:text/csv;charset=utf-8";
    const headers = ['Name', 'Email', 'NTId', 'Phone number', 'Other phone numbers', 'Country', 'Country ISO code', 'Latitude', 'Longitude', 'Tracked on'].join(',');
    const exportData = this.controller.getSelectedUsersLocations().map(x => `${formatCsv(x.user.fullName)},${formatCsv(x.user.email)},${formatCsv(x.user.loginName)},${formatCsv(x.user.phoneNumber)},${x.user.otherPhoneNumbers.map(otherPhoneNumber => formatCsv(otherPhoneNumber)).join(" ")},${formatCsv(x.location?.country?.name)},${formatCsv(x.location?.country?.isoCode3)},${formatCsv(x.location?.coordinates[0]?.toString())},${formatCsv(x.location?.coordinates[1]?.toString())},${formatCsv(x.location?.timeStamp)}`).join("\n");
    const url = encodeURI(`${fileMimeType},${headers}\n${exportData}`);
    try {
      downloadFile(url, `UsersLocations_${moment.utc().format("YYYY-MM-DD-HH-mm")}.csv`);
    }
    finally {
      URL.revokeObjectURL(url);
    }
    return true;
  }

  async copyToClipboard() {
    const copyData = this.controller.getSelectedUsersLocations().map(x => `${formatEmpty(x.user.fullName)}<${formatEmpty(x.user.email)}>`).join(";");
    await navigator.clipboard.writeText(copyData);
    this.toastHelper.toast("Selected rows have been copied into clipboard.", "Rows copied", ToastType.Success, ToastFormat.Text);
    return true;
  }

  async requestUsersLocations() {
    const results = await this.controller.requestUsersLocations();
    const sendResultNo = results.filter(x => x.state.value === 1).length;
    const notAvailableResultNo = results.filter(x => x.state.value === 4).length;
    let toastMessage: string = null;
    let toastType: ToastType = null;
    if (sendResultNo === 0 && notAvailableResultNo === 0) {
      toastMessage = "No location request has been sent.";
      toastType = ToastType.Warning;
    }
    else if (sendResultNo > 0 && notAvailableResultNo === 0) {
      toastMessage = sendResultNo === 1 ? "Location request has been successfully sent." : `All ${sendResultNo} location requests have been successfully sent.`;
      toastType = ToastType.Success;
    }
    else if (sendResultNo === 0 && notAvailableResultNo > 0) {
      toastMessage = notAvailableResultNo === 1 ? "Location request is not available." : `All ${notAvailableResultNo} location requests are not available.`;
      toastType = ToastType.Warning;
    }
    else {
      toastMessage = `${sendResultNo} location request${sendResultNo === 1 ? " has" : "s have"} been successfully sent.\n\n${notAvailableResultNo} location request${notAvailableResultNo === 1 ? " is" : "s are"} not available.`;
      toastType = ToastType.Warning;
    }
    this.toastHelper.toast(toastMessage, "Locations requests results", toastType, ToastFormat.Text);
    return true;
  }

  async sendMessage() {
    return true;
  }

  async sendEmail() {
    const selectedUsers = this.controller.getSelectedUsersLocations().map(x => ({ name: x.user.fullName, emailAddress: x.user.email } as MailAddressModel));
    const usersMissingEmailAddress = selectedUsers.filter(x => x.emailAddress == null);
    if (usersMissingEmailAddress.length === 0 || await this.dialogHelper.confirm(`Following user${usersMissingEmailAddress.length > 1 ? 's are missing' : ' is missing'} an email address: ${usersMissingEmailAddress.map(x => x.name).join(', ')}\n\nDo you wish to continue?`, 'Missing email address')) {
      const dialogModel = {
        from: { name: this.controller.features.fromDisplayName, emailAddress: this.controller.features.fromEmailAddress } as MailAddressModel,
        subject: null,
        message: null
      } as UsersEmail;
      const dialogResult = (await this.dialogHelper.open<UsersEmail, UsersEmail>(UsersEmailDialog, dialogModel));
      if (!dialogResult.wasCancelled) {
        await this.controller.sendUsersEmail(dialogResult.output, selectedUsers.filter(x => x.emailAddress != null));
        this.toastHelper.toast("Email has been sent.", "Email sent", ToastType.Success, ToastFormat.Text);
      }
    }
    return true;
  }

  showDetail(userLocation: UserLocation) {
    this.controller.detailDisplayedInList(userLocation);
    return true;
  }

  zoomInMap(userLocation: UserLocation) {
    if (userLocation != null) {
      this.controller.detailDisplayedInList(userLocation);
      this.controller.zoomUserLocationInMap(userLocation);
    }
    return true;
  }

  showDetailInList(userLocation: UserLocation) {
    const row = this.usersLocationsTable.querySelector(`tbody>tr[data-row-index="${userLocation.rowIndex}"]`);
    if (row != null) {
      row.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  highlightUserLocation(userLocation: UserLocation) {
    userLocation.isHighlighted = true;
  }

  unhighlightUserLocation(userLocation: UserLocation) {
    userLocation.isHighlighted = false;
  }
}
